import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { getToken, handleCallback, initiateLogin, clearToken, logout } from './lib/auth';
import { LanguageProvider } from './lib/i18n';
import { Layout } from './components/Layout';
import { Dashboard } from './pages/Dashboard';
import { Reports } from './pages/Reports';
import { Onboarding } from './pages/Onboarding';
import { TeamOnboarding } from './pages/TeamOnboarding';
import { Billing } from './pages/Billing';
import { Settings } from './pages/Settings';
import { Training } from './pages/Training';

const qc = new QueryClient({
  defaultOptions: {
    queries: {
      // Never retry 4xx errors — they are deterministic client/auth failures
      // that will not resolve on their own. Retrying only wastes 1s+2s+4s=7s.
      retry: (failureCount, error: any) => {
        const status = error?.response?.status ?? error?.status;
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
    },
  },
});

type AuthState = 'loading' | 'authenticated' | 'unauthenticated' | 'error';

export default function App() {
  // Public routes — rendered BEFORE any Cognito machinery. Agents arriving
  // via the team-training invite mail have no portal login and must not be
  // redirected to Cognito. The path test happens at module-import time so
  // none of the auth effects below ever fire on a training URL.
  const isPublicTraining = typeof window !== 'undefined'
    && window.location.pathname.startsWith('/training/');
  if (isPublicTraining) {
    return (
      <LanguageProvider>
        <QueryClientProvider client={qc}>
          <BrowserRouter>
            <Routes>
              <Route path="/training/:token" element={<Training />} />
            </Routes>
          </BrowserRouter>
        </QueryClientProvider>
      </LanguageProvider>
    );
  }

  const [auth, setAuth]           = useState<AuthState>('loading');
  const [authError, setAuthError] = useState<string | null>(null);
  const processingCode = React.useRef(false);

  useEffect(() => {
    async function init() {
      const params = new URLSearchParams(window.location.search);
      const code   = params.get('code');
      const error  = params.get('error');

      console.log('[auth] App init. URL code:', !!code, 'error:', !!error, 'current state:', auth);

      if (error) {
        const desc = params.get('error_description') ?? error;
        console.error('[auth] Cognito error:', desc);
        // Clean URL before showing error
        window.history.replaceState({}, document.title, '/');
        setAuthError(`Cognito fout: ${desc}`);
        setAuth('error');
        return;
      }

      if (code) {
        if (processingCode.current) {
          console.warn('[auth] Already processing a code, skipping duplicate attempt.');
          return;
        }
        processingCode.current = true;

        // Clean the URL immediately so a page-refresh doesn't reuse the code
        window.history.replaceState({}, document.title, '/');
        console.log('[auth] Exchanging code for tokens...');
        
        const result = await handleCallback(code);
        if (result.ok) {
          console.log('[auth] Token exchange successful');
          sessionStorage.removeItem('xt_auth_retried');
          setAuth('authenticated');
        } else {
          console.error('[auth] Token exchange failed:', result.error);
          // invalid_grant almost always means a stale Cognito session cookie.
          // Self-heal: logout once to clear the stale session and retry.
          if (result.error === 'invalid_grant') {
            const alreadyRetried = sessionStorage.getItem('xt_auth_retried');
            if (!alreadyRetried) {
              console.warn('[auth] invalid_grant detected — clearing stale Cognito session (auto-retry)');
              sessionStorage.setItem('xt_auth_retried', '1');
              logout();
              return;
            }
          }
          setAuthError(result.error ?? 'Token uitwisseling mislukt (onbekende fout)');
          setAuth('error');
        }
        return;
      }

      // Check for existing valid token
      const token = getToken();
      if (token) {
        setAuth('authenticated');
      } else {
        setAuth('unauthenticated');
      }
    }

    init();
  }, []);

  // Only auto-redirect when genuinely unauthenticated (not after a failed exchange)
  useEffect(() => {
    if (auth === 'unauthenticated') {
      initiateLogin();
    }
  }, [auth]);

  // ── Error screen ──────────────────────────────────────────────────────────
  if (auth === 'error') {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', alignItems: 'center', justifyContent: 'center', background: '#f9fafb', fontFamily: 'system-ui, sans-serif' }}>
        <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,.08)', border: '1px solid #e5e7eb', padding: '2rem', maxWidth: 440, width: '100%' }}>
          <h2 style={{ margin: '0 0 .5rem', fontSize: '1.1rem', fontWeight: 600, color: '#111' }}>Aanmelden mislukt</h2>
          <p style={{ margin: '0 0 1.25rem', fontSize: '.875rem', color: '#6b7280', wordBreak: 'break-word' }}>
            {authError}
          </p>
          <button
            onClick={() => { clearToken(); setAuthError(null); setAuth('unauthenticated'); }}
            style={{ background: '#0057c7', color: '#fff', border: 'none', borderRadius: 8, padding: '.5rem 1rem', fontSize: '.875rem', fontWeight: 500, cursor: 'pointer' }}
          >
            Opnieuw proberen
          </button>
        </div>
      </div>
    );
  }

  // ── Loading / redirecting ─────────────────────────────────────────────────
  if (auth === 'loading' || auth === 'unauthenticated') {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', alignItems: 'center', justifyContent: 'center', background: '#f9fafb', fontFamily: 'system-ui, sans-serif' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', color: '#9ca3af' }}>
          <div style={{ width: 36, height: 36, border: '3px solid #e5e7eb', borderTopColor: '#0057c7', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          <p style={{ margin: 0, fontSize: '.875rem' }}>Aanmelden...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ── Authenticated app ─────────────────────────────────────────────────────
  // ErrorBoundary catches uncaught render errors anywhere below it, ships
  // them to Sentry, and shows a fallback. Without this, a thrown component
  // turns the whole page white — and the user has to refresh blind.
  return (
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => (
        <div style={{ display: 'flex', minHeight: '100vh', alignItems: 'center', justifyContent: 'center', background: '#f9fafb', fontFamily: 'system-ui, sans-serif' }}>
          <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,.08)', border: '1px solid #e5e7eb', padding: '2rem', maxWidth: 480, width: '100%' }}>
            <h2 style={{ margin: '0 0 .5rem', fontSize: '1.1rem', fontWeight: 600, color: '#111' }}>Er ging iets mis</h2>
            <p style={{ margin: '0 0 1.25rem', fontSize: '.875rem', color: '#6b7280' }}>
              Onverwachte fout. We hebben automatisch een melding ontvangen. Probeer opnieuw te laden of meld je opnieuw aan.
            </p>
            <button onClick={resetError} style={{ background: '#0057c7', color: '#fff', border: 'none', borderRadius: 8, padding: '.5rem 1rem', fontSize: '.875rem', fontWeight: 500, cursor: 'pointer' }}>
              Opnieuw proberen
            </button>
          </div>
        </div>
      )}
    >
      <LanguageProvider>
        <QueryClientProvider client={qc}>
          <BrowserRouter>
            <Routes>
              <Route element={<Layout />}>
                <Route index element={<Dashboard />} />
                <Route path="reports"     element={<Reports />} />
                <Route path="onboarding"  element={<Onboarding />} />
                <Route path="team"        element={<TeamOnboarding />} />
                <Route path="billing"     element={<Billing />} />
                <Route path="settings"    element={<Settings />} />
                <Route path="*"        element={<Navigate to="/" replace />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </QueryClientProvider>
      </LanguageProvider>
    </Sentry.ErrorBoundary>
  );
}
